import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import AutocompleteInput from "../AutocompleteInput";
import DateInput from "../DateInput";
import ButtonCommon from "../ButtonCommon";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import SelectInput from "../SelectInput";
import MainHeader from "../MainHeader";
import { useNavigate } from "react-router-dom";
import { GET, PUT } from "../../api/ApiService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EncryptionHelper from "../EncryptionHelper";
import TextAreaInput from "../TextAreaInput";
import TextInput from "../TextInput";

/** ✅ Convert "05:20 AM" or "11:20 PM" to "HH:mm" (24hr) */
const convertTo24Hour = (timeStr) => {
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours, 10);
  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

const EditDar = () => {
  const navigate = useNavigate();
  const [activityDropdown, setActvityDropdwon] = useState();
  const userDetails = useSelector((state) => state.userReducer.userDetails);
  const caseDropdown = useSelector((state) => state.allcaseReducer.allcase);
  const editData = useSelector((state) => state.editDarReducer.editDar);

  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: "",
    data: null,
  });

  useEffect(() => {
    getActivityList();
  }, []);

  const getActivityList = async () => {
    try {
      const response = await GET("daily-activity/activity-list");
      setActvityDropdwon(response);
    } catch (err) {
      console.error("Error fetching activity list:", err?.response?.data);
      toast.error(err?.response?.data?.message);
    }
  };

  const calculateTimeDifference = (startTime, finishTime) => {
    const start = new Date(startTime);
    const finish = new Date(finishTime);
    const diffMs = finish - start;
    return diffMs / (1000 * 60 * 60);
  };

  const startValue = editData?.StartTime;
  const endValue = editData?.FinishTime;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      case: { value: editData?.CaseID, label: editData?.CaseName },
      activity: editData?.ServiceOrActivity || "",
      activityStart: startValue || "",
      activityEnd: endValue || "",
      subject: editData?.DarSubject || "",
      note: editData?.DarNote || "",
    },
    validationSchema: Yup.object({
      case: Yup.object().required("Case is required"),
      activity: Yup.string().required("Activity is required"),
      activityStart: Yup.date()
        .required("Start date is required")
        .test(
          "is-within-24-hours",
          "Start date cannot be more than 24 hours before the end date",
          function (value) {
            const { activityEnd } = this.parent;
            return activityEnd
              ? value >=
                  new Date(activityEnd).setHours(
                    new Date(activityEnd).getHours() - 24
                  )
              : true;
          }
        ),
      activityEnd: Yup.date()
        .required("End date is required")
        .min(Yup.ref("activityStart"), "End date must be after start date"),
    }),
    onSubmit: (values, { resetForm }) => {
      if (editData?.Exported === 1) {
        toast.error(
          "This activity cannot be changed because it has already been billed."
        );
        return;
      }

      const timeDiff = calculateTimeDifference(
        values.activityStart,
        values.activityEnd
      );

      if (timeDiff === 0) {
        setErrorModal({
          open: true,
          message: "The Start and End dates must be different.",
        });
        return;
      }

      if (timeDiff > 5) {
        setConfirmModal({
          open: true,
          message:
            "Are you sure you want to create an activity that is longer than 5 hours?",
          data: { values, resetForm },
        });
        return;
      }

      handleSave(values, resetForm);
    },
  });

  const handleSave = (values, resetForm) => {
    const putData = {
      CaseID: values.case.value,
      FinishTime: values.activityEnd,
      Exported: false,
      ServiceOrActivity: values.activity,
      StartTime: values.activityStart,
      ModifiedByUserID: userDetails?.id,
      Subject: values.subject,
      Note: values.note,
    };

    console.log("🟨 data to be encrypted =>", putData);

    const encryptData = EncryptionHelper.encryptData(putData);

    PUT(`daily-activity/update?id=${editData?.ReportID}`, { encryptData })
      .then((resp) => {
        toast.success(resp?.message);
        navigate(-1);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  const handleProceedConfirm = () => {
    if (confirmModal.data) {
      handleSave(confirmModal.data.values, confirmModal.data.resetForm);
    }
    setConfirmModal({ open: false, message: "", data: null });
  };

  return (
    <Box>
      <MainHeader headingText="Update DAR" />
      <Stack
        p={4}
        gap={4}
        sx={{
          border: "1px solid #C1C1C1",
          minHeight: "80vh",
        }}
      >
        <Stack gap={4}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "700",
              lineHeight: "21.92px",
              textAlign: "left",
            }}
          >
            Update Daily Activity Report
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              {/* Case */}
              <Grid item xs={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Case<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <AutocompleteInput
                    id="case"
                    name="case"
                    options={caseDropdown}
                    value={formik.values.case}
                    onChange={(event, newValue) =>
                      formik.setFieldValue("case", newValue)
                    }
                    disabled={true}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.case && formik.errors.case && (
                    <Typography color="red" fontSize="small">
                      {formik.errors.case}
                    </Typography>
                  )}
                </Stack>
              </Grid>

              {/* Activity */}
              <Grid item xs={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Select Activity<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <SelectInput
                    name="activity"
                    placeholder=""
                    onChangeValue={formik.handleChange}
                    onBlurValue={formik.handleBlur}
                    value={formik.values.activity}
                    dropDownData={activityDropdown}
                  />
                  {formik.touched.activity && formik.errors.activity && (
                    <Typography color="red" fontSize="small">
                      {formik.errors.activity}
                    </Typography>
                  )}
                </Stack>
              </Grid>

              {/* Start Time */}
              <Grid item xs={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Activity Start Date & Time
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <DateInput
                    type="datetime-local"
                    name="activityStart"
                    placeholder="activityStart"
                    onChangeValue={formik.handleChange}
                    onBlurValue={formik.handleBlur}
                    value={formik.values.activityStart}
                  />
                  {formik.touched.activityStart &&
                    formik.errors.activityStart && (
                      <Typography color="red" fontSize="small">
                        {formik.errors.activityStart}
                      </Typography>
                    )}
                </Stack>
              </Grid>

              {/* End Time */}
              <Grid item xs={6}>
                <Stack>
                  <Typography sx={{ textAlign: "left" }}>
                    Activity End Date & Time
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <DateInput
                    type="datetime-local"
                    name="activityEnd"
                    placeholder="activityEnd"
                    onChangeValue={formik.handleChange}
                    onBlurValue={formik.handleBlur}
                    value={formik.values.activityEnd}
                  />
                  {formik.touched.activityEnd && formik.errors.activityEnd && (
                    <Typography color="red" fontSize="small">
                      {formik.errors.activityEnd}
                    </Typography>
                  )}
                </Stack>
              </Grid>

              {/* Subject */}
              <Grid item xs={6}>
                <Typography sx={{ textAlign: "left" }}>Subject</Typography>
                <TextInput
                  name="subject"
                  placeholder=""
                  onChangeValue={formik.handleChange}
                  onBlurValue={formik.handleBlur}
                  value={formik.values.subject}
                />
              </Grid>

              {/* Note */}
              <Grid item xs={6}>
                <Typography sx={{ textAlign: "left" }}>Note</Typography>
                <TextAreaInput
                  name="note"
                  placeholder=""
                  onChangeValue={formik.handleChange}
                  onBlurValue={formik.handleBlur}
                  value={formik.values.note}
                  rows={10}
                />
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <Stack direction="row" gap={4}>
                  <ButtonCommon type="submit">Save</ButtonCommon>
                  <ButtonCommon
                    btnClicked={() => {
                      navigate(-1);
                    }}
                  >
                    Cancel
                  </ButtonCommon>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Stack>
      </Stack>

      {/* Error Modal */}
      <Dialog
        open={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: "" })}
      >
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Typography>{errorModal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModal({ open: false, message: "" })}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={() =>
          setConfirmModal({ open: false, message: "", data: null })
        }
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <Typography>{confirmModal.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmModal({ open: false, message: "", data: null })
            }
          >
            Cancel
          </Button>
          <Button onClick={handleProceedConfirm} variant="contained">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(EditDar);
